import { Polygon } from 'geojson';
import { groupBy, orderBy } from 'lodash';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { log } from '@methanesat/log';
import { bboxPolygon, booleanIntersects, polygon } from '@turf/turf';

import { ONE_MSAT_DATE_RANGE } from '../../../environmentVariables';
import {
    selectAreaEmissionsWithinZoom,
    selectEmissionsMapBBox,
    selectGlobalDate,
    selectMethaneProduct,
    selectPlatform,
    selectTargetDates,
    selectUrlParamsInitialized
} from '../../../reducers';
import { useGetStacSearchQuery } from '../../../reducers/api';
import {
    AreaEmissionsProducts,
    CaptureFeatureProperties,
    CapturePlusCount,
    Platforms,
    PointSourceStacResponse,
    STACCollection,
    SortDirection,
    StacFeature,
    StacDateBlock,
    StacSearch,
    StacSearchResponse,
    isDPPCaptureFeature,
    StacFilterOperator,
    StacFilter
} from '../../../types';
import {
    customFetch,
    filterL4CapturesWithL3,
    filterPointCapturesIfParentPresent,
    getBasicSearch,
    getCollectionNameArray,
    getDateRangeFromPlatform,
    getDayEnd,
    getIsValidDateString,
    getProductFromCollectionId,
    getStacDataSource,
    getTimeBoundStacSearch,
    groupFeaturesByMSATCollection,
    modifySearchCollection,
    captureFeaturesToDateBlocks,
    normalizeTargetIds
} from '../../../utils';
import { getTimeSelectorTimespan, TARGET_GROUPS } from '../../../consts';

/**
 * This file should contain ALL direct calls to STAC so we can
 * easily see which endpoints we are using.
 *
 * Write helper functions here and call them where they are needed.
 */

interface FeatureGroup {
    datetime: string;
    targetOrGroupId: string;
    stacItems: StacFeature<Polygon, CaptureFeatureProperties>[];
}
interface FeaturesGroupedByMsatCollection {
    [collection_id: string]: FeatureGroup;
}
/**
 * Returns a list of target-captures for which to display data.
 * Returns a single capture/flight per target.
 * If a target has a specific date selected (in @param targetDates),
 * the capture/flight for that date will be returned.
 * If no target-specific date is given, the latest capture will be returned.
 */

export const getCapturesToDisplay = (
    features: StacFeature<Polygon, CaptureFeatureProperties>[] | undefined,
    targetDates: { [targetId: string]: number | null | undefined },
    product?: AreaEmissionsProducts
) => {
    const result: { [key in AreaEmissionsProducts]: CapturePlusCount[] } = {
        [AreaEmissionsProducts.l3]: [],
        [AreaEmissionsProducts.l4]: []
    };

    if (!features || features.length === 0) return result;

    // Group features by L3 and L4
    const groupedByProcessingLevel = groupBy(features, (capture) => getProductFromCollectionId(capture.collection));

    // If L3 is being shown, then filter for the L4 captures that have corresponding L3 captures.
    if (product === AreaEmissionsProducts.l3) {
        groupedByProcessingLevel[AreaEmissionsProducts.l4] = filterL4CapturesWithL3(
            groupedByProcessingLevel[AreaEmissionsProducts.l4],
            groupedByProcessingLevel[AreaEmissionsProducts.l3]
        );
    }

    /**
     * For each processing level (L3 and L4), group data by target_id (or target_group_id)
     * and choose a single collection to display - either most recent or the date selected for the
     * given target.
     */
    for (const level of [AreaEmissionsProducts.l3, AreaEmissionsProducts.l4]) {
        const features = groupedByProcessingLevel[level] || [];
        if (features.length === 0) continue;

        const collectionGroups: FeaturesGroupedByMsatCollection = groupFeaturesByMSATCollection(features);

        // { targetId: { collectionId: { datetime, targetId, collectionId } } }
        const targetGroups: { [targetId: string]: { [collectionId: string]: FeatureGroup } } = {};
        Object.entries(collectionGroups).forEach(([collectionId, collectionGroup]: [string, FeatureGroup]) => {
            const targetOrGroupId = collectionGroup.targetOrGroupId;
            if (targetGroups[targetOrGroupId]) {
                targetGroups[targetOrGroupId] = {
                    ...targetGroups[targetOrGroupId],
                    [collectionId]: collectionGroup
                };
            } else {
                targetGroups[targetOrGroupId] = { [collectionId]: collectionGroup };
            }
        });

        for (const [targetId, targetCaptures] of Object.entries(targetGroups)) {
            let stacItems: StacFeature<Polygon, CaptureFeatureProperties>[] = [];

            if (targetDates[targetId]) {
                // if the target has a specific date set,
                // find the collection in the target group that matches this date, if possible
                const collectionGroupToDisplay: [collectionId: string, collectionGroup: FeatureGroup] | undefined =
                    Object.entries(targetCaptures).find(([, collectionGroup]) => {
                        const isValidDateString = getIsValidDateString(collectionGroup.datetime);
                        if (isValidDateString && collectionGroup.datetime) {
                            const captureDate = new Date(collectionGroup.datetime);
                            const captureDateEOD = getDayEnd(captureDate).getTime();
                            return captureDateEOD === targetDates[targetId];
                        }
                        return false;
                    });
                stacItems = (collectionGroupToDisplay && collectionGroupToDisplay[1].stacItems) ?? [];
            } else {
                // otherwise, get the most recent feature
                const sorted: [collectionId: string, FeatureGroup][] = orderBy(
                    Object.entries(targetCaptures),
                    ([, collectionGroup]) => (collectionGroup.datetime ? new Date(collectionGroup.datetime) : 0),
                    'desc'
                );
                const [, collectionGroup] = sorted[0];
                stacItems = collectionGroup.stacItems ?? [];
            }

            if (stacItems) {
                // add a `monthlyCaptureCount` to each STAC item
                stacItems.forEach((item) => {
                    const updatedItem: CapturePlusCount = {
                        ...item,
                        properties: {
                            ...item.properties,
                            monthlyCaptureCount: targetCaptures.length
                        }
                    };
                    result[level].push(updatedItem);
                });
            }
        }
    }

    // Create a map of L4 targetId's to net_total or mean_total flux value depending on if it is a DPP capture feature
    const l4NetTotalMap = new Map<string, number>();
    const l4MeanTotalFluxMap = new Map<string, number>();

    result[AreaEmissionsProducts.l4].forEach((capture) => {
        if (isDPPCaptureFeature(capture.properties)) {
            l4MeanTotalFluxMap.set(capture.properties.target_id, capture.properties?.mean_total_flux || 0);
        } else {
            l4NetTotalMap.set(capture.properties.target_id, capture.properties?.net_total || 0);
        }
    });

    // Update L3 captures with matching L4 net_total values
    result[AreaEmissionsProducts.l3] = result[AreaEmissionsProducts.l3].map((capture) => {
        const targetId = capture.properties?.target_id;

        if (targetId) {
            if (l4MeanTotalFluxMap.has(targetId)) {
                return {
                    ...capture,
                    properties: {
                        ...capture.properties,
                        net_total: l4MeanTotalFluxMap.get(targetId) ?? 0
                    }
                };
            } else if (l4NetTotalMap.has(targetId)) {
                return {
                    ...capture,
                    properties: {
                        ...capture.properties,
                        net_total: l4NetTotalMap.get(targetId) ?? 0
                    }
                };
            }
        }

        return capture;
    });

    return result;
};

/**
 * Get point source GeoJSON for one stac item
 *
 * TODO: DP-3560 This feels like a potential risk. Add verification that the url is ours
 */
export const getPointSourceItem = async ({
    abortController,
    pointSourceUrl,
    stacCollectionId,
    pointItemId,
    relatedRasterItemId,
    targetId
}: {
    abortController: AbortController;
    pointSourceUrl?: string;
    stacCollectionId: STACCollection;
    pointItemId: string;
    relatedRasterItemId?: string;
    targetId: string;
}): Promise<PointSourceStacResponse | null> => {
    // Not all collects have point sources (either none detected or failed QA).
    if (!pointSourceUrl) {
        return null;
    }

    // Don't try to fetch from private google cloud buckets
    if (pointSourceUrl.startsWith('gs')) {
        log.error(`Invalid private gs url for ${pointItemId}.  Could not retrieve Point Sources.`);
        return null;
    }

    try {
        const response = await customFetch(pointSourceUrl, { signal: abortController.signal });
        if (response.status < 200 || response.status >= 400) {
            log.error(`${response.status} received from cloud. Could not retrieve Point Sources`);
            return null;
        }
        const stacResponse = (await response.json()) as StacSearchResponse;

        // Since this data is fetched via customFetch and isn't passing through RTK query, target IDs need to be normalized here
        normalizeTargetIds(stacResponse?.features);

        return {
            ...stacResponse,
            id: pointItemId,
            collection: stacCollectionId,
            parentTargetId: targetId,
            ...(relatedRasterItemId && { relatedRasterItemId })
        };
    } catch (e) {
        const isAbortError = !!`${e}`.match(`AbortError|Hook cleanup`);
        if (isAbortError) {
            // User navigated away, which cancelled the request.
            return null;
        }
        log.error(`Error (getPointSourceItem): ${e}`);
        return null;
    }
};

export const getStacItem = async (itemId: string, collectionId: STACCollection) => {
    const urls = getStacDataSource();
    const itemDataResponse = await customFetch(`${urls.targetsUrl}/collections/${collectionId}/items/${itemId}`);
    const data: StacFeature = await itemDataResponse.json();
    return data;
};

/**
 * This should run on L4 data only.
 */
export const useGetHistoricalSamplesForTarget = (
    targetGroupId: string | null | undefined,
    platformToUse?: Platforms
): Record<string, StacDateBlock[]> | null => {
    const historicalSamplesRef = useRef<StacSearchResponse<Polygon, CaptureFeatureProperties> | null>(null);
    let platform = useSelector(selectPlatform);
    const product = useSelector(selectMethaneProduct);
    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);
    const timespan = getTimeSelectorTimespan(platform);
    if (platformToUse) platform = platformToUse;

    let search: StacSearch | null = modifySearchCollection(platform, product);

    if (targetGroupId) {
        const targetGroupMembers = TARGET_GROUPS[targetGroupId];

        /**
         * Note: search object is in the shape of the object for STAC search POST request.
         * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
         * See also https://github.com/stac-api-extensions/filter for GET and POST versions of many filter operators
         */
        const filter: StacFilter = targetGroupMembers
            ? {
                  op: StacFilterOperator.or,
                  args: targetGroupMembers.map((memberId) => {
                      return {
                          op: StacFilterOperator.eq,
                          args: [{ property: 'target_id' }, memberId]
                      } as StacFilter;
                  })
              }
            : {
                  op: StacFilterOperator.eq,
                  args: [
                      {
                          property: 'target_id'
                      },
                      targetGroupId
                  ]
              };
        search = {
            ...getBasicSearch(),
            filter,
            collections: getCollectionNameArray(platform, product),
            sortby: [
                {
                    field: 'properties.end_datetime',
                    direction: SortDirection.Ascending
                }
            ],
            limit: 1000
        };
    }

    // Get all data for the target.
    const { data } = useGetStacSearchQuery(
        { stacSearch: search },
        {
            skip: !targetGroupId || !search || !urlParamsInitialized
        }
    ) as {
        data?: StacSearchResponse<Polygon, CaptureFeatureProperties>;
    };

    if (data?.features) {
        // Filter out the `point_pruned` STAC items
        const filteredPortalCaptures = filterPointCapturesIfParentPresent(data.features);

        const groupedByProcessingLevel = groupBy(filteredPortalCaptures, (capture) =>
            getProductFromCollectionId(capture.collection)
        );
        const l4Features = groupedByProcessingLevel[AreaEmissionsProducts.l4] || [];
        const l3Features = groupedByProcessingLevel[AreaEmissionsProducts.l3] || [];
        const l4FeaturesWithL3 = filterL4CapturesWithL3(l4Features, l3Features);

        // Ensure consistent chronological order after shuffling
        filteredPortalCaptures.sort(sortByDatetime);
        l4FeaturesWithL3.sort(sortByDatetime);

        historicalSamplesRef.current = {
            ...data,
            features: product === AreaEmissionsProducts.l3 ? l4FeaturesWithL3 : filteredPortalCaptures
        };

        return captureFeaturesToDateBlocks(
            product === AreaEmissionsProducts.l3 ? l4FeaturesWithL3 : filteredPortalCaptures,
            timespan
        );
    }

    if (historicalSamplesRef.current?.features) {
        return captureFeaturesToDateBlocks(historicalSamplesRef.current.features, timespan);
    }
    return null;
};

export const useGetCapturesWithinGlobalTimeRange = (product: AreaEmissionsProducts) => {
    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);
    const platform = useSelector(selectPlatform);
    const globalDate = useSelector(selectGlobalDate);
    const stacSearch = useMemo(() => {
        const dateRange = getDateRangeFromPlatform(platform);
        const initialSearch = modifySearchCollection(platform, product);

        if (urlParamsInitialized) {
            return ONE_MSAT_DATE_RANGE && platform === Platforms.MSAT
                ? initialSearch
                : getTimeBoundStacSearch(new Date(globalDate), initialSearch, dateRange);
        }
        return null;
    }, [platform, globalDate, urlParamsInitialized, product]);

    // Fetches the layer data that is passed to the `targetLayers` and `methaneLayers` hooks
    const { data: featureList = null, isFetching } = useGetStacSearchQuery(
        { stacSearch },
        {
            skip: !stacSearch || !urlParamsInitialized
        }
    );
    return { featureList, isFetching };
};

/**
 * @returns map of all target ids with corresponding number of times it appears in data
 */
export const useGetAllTargetsWithNumberOfCaptures = () => {
    const platform = useSelector(selectPlatform);
    const product = useSelector(selectMethaneProduct);
    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);

    const stacSearch = useMemo(() => {
        const search = modifySearchCollection(platform, product);
        // Filter out point source collections
        search.collections = search.collections?.filter((c) => !c.includes('point'));
        return search;
    }, [platform, product]);

    const { data: featureList } = useGetStacSearchQuery({ stacSearch }, { skip: !urlParamsInitialized });

    const targetsWithCount = new Map();
    featureList?.features?.map((f) => {
        const targetOrGroupId = f.properties.target_group_id || f.properties.target_id;
        if (targetsWithCount.has(targetOrGroupId)) {
            targetsWithCount.set(targetOrGroupId, targetsWithCount.get(targetOrGroupId) + 1);
        } else {
            targetsWithCount.set(targetOrGroupId, 1);
        }
    });

    return targetsWithCount;
};

/**
 * Used to determine when to show mini target date selectors -
 * i.e. when only one target is within the current viewport
 *
 * @param captures The list of captures currently loaded, based on global date selection & target-specific dates
 * @returns The list of captures loaded that are visible within the map's viewport.
 *
 */
export const useGetCapturesWithinViewport = (
    captures: StacSearchResponse<Polygon, CaptureFeatureProperties> | null
) => {
    const targetDates = useSelector(selectTargetDates);
    const bbox = useSelector(selectEmissionsMapBBox);
    const product = useSelector(selectMethaneProduct);
    const areaEmissionsWithinZoom = useSelector(selectAreaEmissionsWithinZoom);

    const capturesWithinViewport = useMemo(() => {
        if (!captures || !captures.features) return [];
        const viewportPolygon = bboxPolygon(bbox);
        const capturesDisplayed = getCapturesToDisplay(captures.features, targetDates);

        const capturesToUse = areaEmissionsWithinZoom
            ? capturesDisplayed[product] // for area emissions, use the captures for the current product
            : product === AreaEmissionsProducts.l3 // for L3, at target level, use L4 captures that have corresponding L3 captures
              ? filterL4CapturesWithL3(
                    capturesDisplayed[AreaEmissionsProducts.l4],
                    capturesDisplayed[AreaEmissionsProducts.l3]
                )
              : capturesDisplayed[AreaEmissionsProducts.l4]; // for L4, at target level, use L4 captures

        return filterPointCapturesIfParentPresent(capturesToUse).filter((capture) => {
            const capturePolygon = polygon(capture.geometry.coordinates);
            return booleanIntersects(capturePolygon, viewportPolygon);
        });
    }, [captures, targetDates, bbox, product, areaEmissionsWithinZoom]);

    return capturesWithinViewport;
};

/**
 * Retrieves the singular capture in view based on the methane product currently active,
 * and the zoom level.
 *
 * @param capturesInView List of all captures in the current viewport
 * @returns The single capture in view, or null if there are more/less than 1
 */
export const useGetSingleCaptureWithinViewport = (capturesInView: CapturePlusCount[]) => {
    const product = useSelector(selectMethaneProduct);
    const areaEmissionsWithinZoom = useSelector(selectAreaEmissionsWithinZoom);

    const processingLevel = product === AreaEmissionsProducts.l3 && areaEmissionsWithinZoom ? 'Level3' : 'Level4';

    const visibleCapturesAtZoomLevel = capturesInView.filter((c) => c.collection.includes(processingLevel));
    return visibleCapturesAtZoomLevel.length === 1 ? visibleCapturesAtZoomLevel[0] : null;
};

// Sort captures by their datetime property from oldest to newest
const sortByDatetime = (a: CapturePlusCount, b: CapturePlusCount) =>
    new Date(a.properties.datetime).getTime() - new Date(b.properties.datetime).getTime();

// Returns a list of all STAC target IDs
export const useTargetList = () => {
    const stacSearch = useMemo(() => {
        const search = getBasicSearch();
        search.collections = [
            STACCollection.MethaneAIR_Level4_Portal,
            STACCollection.MethaneSAT_Level4_Portal,
            STACCollection.MethaneSAT_Level4_Portal_twostep
        ];
        return search;
    }, []);

    const { data: featureList = null } = useGetStacSearchQuery({ stacSearch });

    return [...new Set(featureList?.features?.map((f) => f.properties.target_id))];
};

/**
 * Searches for all L4 STAC items of a target by target ID.
 */
export const useStacSearchByTargetId = (targetId: string) => {
    const search: StacSearch = useMemo(() => {
        return {
            ...getBasicSearch(),
            filter: {
                op: StacFilterOperator.eq,
                args: [{ property: 'target_id' }, targetId]
            },
            collections: [
                STACCollection.MethaneAIR_Level4_Portal,
                STACCollection.MethaneSAT_Level4_Portal,
                STACCollection.MethaneSAT_Level4_Portal_twostep,
                STACCollection.MethaneAIR_Level4_point_pruned,
                STACCollection.MethaneSAT_Level4_point_pruned
            ],
            fields: {
                include: ['bbox', 'properties', 'collection', 'geometry', 'assets'],
                exclude: [
                    'properties.proj:epsg',
                    'properties.proj:wkt2',
                    'properties.proj:transform',
                    'properties.proj:shape',
                    'links'
                ]
            },
            sortby: [
                {
                    field: 'properties.end_datetime',
                    direction: SortDirection.Ascending
                }
            ]
        };
    }, [targetId]);

    const { data } = useGetStacSearchQuery({ stacSearch: search });
    return data?.features;
};

/**
 * Searches for all L4 STAC items by collection id.
 */
export const useStacSearchByCollectionId = (itemId: string) => {
    const search: StacSearch = useMemo(() => {
        return {
            ...getBasicSearch(),
            filter: {
                op: StacFilterOperator.eq,
                args: [{ property: 'collection_id' }, itemId]
            },
            collections: [
                STACCollection.MethaneSAT_Level4_Portal,
                STACCollection.MethaneSAT_Level4_Portal_twostep,
                STACCollection.MethaneSAT_Level4_point_pruned
            ],
            fields: {
                include: ['bbox', 'properties', 'collection', 'geometry', 'assets'],
                exclude: [
                    'properties.proj:epsg',
                    'properties.proj:wkt2',
                    'properties.proj:transform',
                    'properties.proj:shape',
                    'links'
                ]
            },
            sortby: [
                {
                    field: 'properties.end_datetime',
                    direction: SortDirection.Ascending
                }
            ]
        };
    }, [itemId]);

    const { data } = useGetStacSearchQuery({ stacSearch: search });
    return data;
};
